import { Component, OnInit, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonService, ForgotPasswordService } from 'huddyapi';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { TempAuthService, Credentials } from '../../services/tempAuthService';
import { UUIDService } from '../../services/uuid.service';
import { Location } from '@angular/common';
import { DesktopErrorHandler } from '../../services/errorHandler.service';

export enum ResetType {
  RESET_PASSWORD, COMPLETE_REGISTRATION
}

@Component({
  selector: 'app-reset',
  templateUrl: './reset.component.html',
  styleUrls: ['../userManagementPages.scss']
})
export class ResetComponent implements OnInit {
  email: string;
  resetType: ResetType;
  code: string;
  password1: string;
  password2: string;
  page: string;
  uuid: string;
  waiting: string;
  passwordShowing: boolean;

  constructor(
    public router: Router,
    private forgotPassword: ForgotPasswordService,
    private snackBar: MatSnackBar,
    private errorHandler: DesktopErrorHandler,
    private authService: TempAuthService,
    private uuidService: UUIDService,
    private route: ActivatedRoute,
    private location: Location) {
    this.waiting = null;
    this.passwordShowing = false;
    this.uuidService.getUUID().then((uuid: string) => {
      this.uuid = uuid;
    });
  }

  ngOnInit() {
    this.resetType = this.route.snapshot.data?.type;
    if(!this.resetType)
      this.resetType = ResetType.RESET_PASSWORD;
    const email = this.route.snapshot.paramMap.get("email");
    if(this.resetType == ResetType.RESET_PASSWORD && email) {
      this.page = 'activate';
      this.email = email;
    } else
      this.page = 'forgot';
  }

  goBack() {
    if(this.waiting)
      return;
    if(this.page == 'reset') {
      this.page = 'forgot';
      this.code = '';
      this.password1 = '';
      this.password2 = '';
    } else
      this.location.back();
  }

  togglePasswordShowing() {
    this.passwordShowing = !this.passwordShowing;
  }

  getForgotPageTitleText(): string {
    return this.resetType == ResetType.RESET_PASSWORD ? 'Reset Password' : 'Complete Registration';
  }

  getForgotPageDescriptionContext(): string {
    return this.resetType == ResetType.RESET_PASSWORD ? 'password reset' : 'registration';
  }

  getButtonText(): string {
    switch(this.page) {
      case 'forgot':
        return 'Send reset email';
      case 'reset':
        return 'Reset';
      case 'activate':
        return 'Activate';
      default:
        return '';
    }
  }

  canGoNext(): boolean {
    if(this.waiting)
      return;
    switch(this.page) {
      case 'forgot':
        return this.email && this.email.length > 0 && this.email.length <= 255 && this.email.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gi) != null;
      case 'reset':
        return this.code && this.code.length == 4 && this.password1 && this.password1.length > 0
          && this.password1 == this.password2 && this.passwordCharacterCountGood() && this.passwordCapitalizationGood()
          && this.passwordNumberGood() && this.passwordSpecialCharacterGood() && this.passwordsMatch();
      case 'activate':
        return this.code && this.code.length == 4 && this.password1 && this.password1.length > 0;
      default:
        return false;
    }
  }

  passwordCharacterCountGood() {
    return this.password1 && this.password1.length > 7;
  }

  passwordCapitalizationGood() {
    return this.password1 && this.password1.match(/[a-z]/) != null && this.password1.match(/[A-Z]/) != null;
  }

  passwordNumberGood() {
    return this.password1 && this.password1.match(/[0-9]/) != null;
  }

  passwordSpecialCharacterGood() {
    return this.password1 && this.password1.match(/[\`~!@#$%^&*()\-=_+[\]\\{}|;:'",.\/<>?]/) != null;
  }

  passwordsMatch() {
    return this.password1 && this.password1 == this.password2;
  }

  goNext() {
    switch(this.page) {
      case 'forgot':
        this.sendResetEmail();
        break;
      case 'reset':
        this.resetPassword();
        break;
      case 'activate':
        this.activateAccount();
        break;
      default:
    }
  }

  tryGoNext() {
    if(this.canGoNext())
      this.goNext();
  }

  sendResetEmail() {
    if(this.waiting)
      return;
    this.waiting = 'main';
    const email: string = this.email;
    this.forgotPassword.resetPassword(email).subscribe(() => {
      this.page = 'reset';
      this.email = email;
      this.waiting = null;
    }, (error: any) => {
      this.waiting = null;
      this.errorHandler.handleSimple(error,
        (typeof (error.error) == 'string' && error.error.length > 0) ? error.error : (this.resetType == ResetType.RESET_PASSWORD ? 'Error resetting password' : 'Error completing registration')
      );
    });
  }

  resetPassword() {
    if(this.waiting)
      return;
    this.waiting = 'main';
    const data = {
      username: this.email,
      password: this.password1,
      verificationCode: this.code,
      deviceUUID: this.uuid,
      deviceToken: this.uuid
    };
    this.forgotPassword.finish(data).subscribe(() => {
      this.snackBar.open('Password successfully reset', 'Dismiss', {
        duration: 3000
      });
      this.authService.login(new Credentials({
        username: data.username,
        password: data.password,
        deviceUUID: data.deviceUUID
      })).then((user: any) => {
        this.router.navigate(['patients']);
      }, (error: any) => {
        if(typeof error.error == 'string')
          this.errorHandler.handleSimple(error, error.error);
        else
          this.errorHandler.handle(error, 'login', DesktopErrorHandler.COMMON_MESSAGES);
        this.waiting = null;
      });
    }, (error: any) => {
      this.waiting = null;
      this.errorHandler.handle(error, null, {
        default: 'Error resetting password' + ((error.error && typeof error.error == 'string') ? (': ' + error.error) : ''),
        0: (): string => null,
        406: 'You either did not enter the information correctly, your verification code is likely outdated or you have tried too many times. If the latter, wait a little while and try again.'
      });
    });
  }

  activateAccount() {
    if(this.waiting)
      return;
    this.waiting = 'main';
    const data = {
      username: this.email,
      password: this.password1,
      verificationCode: this.code,
      deviceUUID: this.uuid,
      deviceToken: this.uuid
    };
    this.authService.verifyAccount(data).subscribe(() => {
      this.snackBar.open('Your account was successfully activated!', 'Dismiss', {
        duration: 3000
      });
      this.authService.login(new Credentials({
        username: data.username,
        password: data.password,
        deviceUUID: this.uuid
      })).then((user: any) => {
        CommonService.onboardingAllowed = true;
        this.router.navigate(['onboarding']);
      }, (error: any) => {
        if(typeof error.error == 'string')
          this.errorHandler.handleSimple(error, error.error);
        else
          this.errorHandler.handle(error, 'login', DesktopErrorHandler.COMMON_MESSAGES);
        this.waiting = null;
      });
    }, (error: any) => {
      this.waiting = null;
      this.errorHandler.handle(error, null, {
        default: (): string => {
          if(!(error && error.error && typeof error.error == 'string'))
            return 'Error verifying account';
          switch(error.error) {
            case 'Verification code does not match.':
              return 'Incorrect code';
            case 'Password does not match.':
              return 'Incorrect password';
            case 'Device belongs to a different user.':
              return 'This device belongs to a different user';
            case 'Incorrect device.':
              return 'Incorrect device';
            default:
              return 'Error verifying account';
          }
        },
        0: (): string => null
      });
    });
  }

  sendNewCode() {
    if(this.waiting)
      return;
    this.waiting = 'code';
    this.authService.getNewVerificationCode({
      username: this.email,
      resetType: this.page == 'reset' ? 'reset' : 'activation'
    }).subscribe(() => {
      this.waiting = null;
      this.snackBar.open('New code sent', 'Dismiss', {
        duration: 3000
      });
    }, (error: any) => {
      this.waiting = null;
      this.errorHandler.handleSimple(error, 'Error creating account');
    });
  }

  @HostListener('window:keydown.enter', ['$event'])
  handleEnter(event: any) {
    switch(event.target.tagName) {
      case 'BUTTON':
      case 'A':
        return;
      case 'INPUT':
        if(event.target.type == 'checkbox')
          return;
      default:
        event.preventDefault();
        this.tryGoNext();
    }
  }
}
